/**
 * Generated by orval 🍺
 * Do not edit manually.
 * DevSMS API v2
 * The dashboard's API (LLD §26.2), and the source Orval generates the
 * dashboard's client from (`dashboard/orval.config.ts`).
 *
 * This document describes the Go server as built. It is held to the code
 * by `test/contract`: the routes it lists are exactly the routes served,
 * in normal and in standalone mode (`TestV2Routes_MatchTheSpecInBothModes`),
 * and every answer the integration, contract and end-to-end tests provoke
 * is validated against the operation it documents
 * (`test/harness/apispec.Validating`). Spectral lints it with
 * `api/.spectral.yaml` (`make spec`).
 *
 * Conventions:
 *   - JSON is camelCase; enums are snake_case; times are RFC 3339 UTC.
 *   - Money is a whole number of XAF (ISO 4217 exponent 0) in a plain
 *     integer field, with a sibling `currency` field (always `XAF`) on
 *     the objects that carry one. There is no `{amount, currency}`
 *     wrapper object (DV-145).
 *   - Lists that can grow are keyset-paged: `?cursor=&limit=` in,
 *     `{items, nextCursor, hasMore}` out. A cursor is opaque and signed;
 *     a tampered one answers 422 `invalid_cursor`. Short, bounded lists
 *     answer `{items}`.
 *   - Errors are RFC 9457 `application/problem+json`. The machine code is
 *     the last segment of `type`, in kebab case
 *     (`…/problems/wallet-insufficient` is the code `wallet_insufficient`);
 *     `x-problem-codes` lists every code with its status. There is no
 *     separate `code` member (DV-145).
 *   - Authentication is a bearer access token from `POST /auth/login`
 *     (or Google, TOTP, refresh, invite). API keys are not accepted on
 *     v2; they are the v1 credential.
 *   - `x-permission` names the permission an operation requires (every
 *     one listed, when it is a list). Account routes (`/account/*`) also
 *     admit any staff user without it (DV-55). Every `/admin/*` route
 *     also refuses a staff user who has not enrolled TOTP (403
 *     `totp_enrolment_required`).
 *   - `x-step-up: true` marks an operation that needs an `X-Step-Up`
 *     token from `POST /auth/step-up`, under 5 minutes old (403
 *     `step_up_required` otherwise).
 *   - `x-idempotency: required` marks an operation that needs an
 *     `Idempotency-Key` header (422 `idempotency_key_required`); a
 *     repeat replays the first answer byte for byte with
 *     `Idempotent-Replay: true`. `x-idempotency: exempt` marks a write
 *     in a money area that deliberately takes none, and
 *     `x-idempotency-reason` says why.
 *   - `x-standalone: absent` marks an operation that is not mounted when
 *     `STANDALONE_MODE=true` (it answers 404 there).
 *   - `?lang=fr|en` (or `Accept-Language`) chooses the language of every
 *     problem's `detail` and of the PDFs; French is the default.
 *   - CORS admits one origin, the deployment's `DASHBOARD_URL`, with
 *     credentials (none when it is unset; never `*`). Preflights are
 *     answered `204` before authentication and rate limiting. Allowed
 *     request headers: `Authorization`, `Content-Type`,
 *     `Accept-Language`, `Idempotency-Key`, `X-Step-Up`, `X-Sdk-Client`;
 *     exposed: `Idempotent-Replay`, `RateLimit-Limit`,
 *     `RateLimit-Remaining`, `RateLimit-Reset`, `Retry-After`,
 *     `X-Request-Id`, `Content-Disposition`. (`/api/v1` keeps Nest's
 *     `enableCors()`: `Access-Control-Allow-Origin: *`, no credentials.)
 *
 * OpenAPI spec version: 2.0.0
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  AdminListPaymentsParams,
  AdminListPaymentsToReviewParams,
  AdminPayment,
  AdminPaymentDetail,
  AdminPaymentPage,
  AdminResolvePaymentBody,
  ConflictResponse,
  ForbiddenResponse,
  InternalErrorResponse,
  NotFoundResponse,
  PayloadTooLargeResponse,
  PaymentMethod,
  ReceivePaymentCallbackBody,
  ReceivePaymentCallbackPutBody,
  TooManyRequestsResponse,
  UnauthorizedResponse,
  UnavailableResponse,
  UnprocessableResponse
} from '../model';

import { apiFetch } from '../../mutator.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getReceivePaymentCallbackUrl = (method: PaymentMethod,) => {




  return `/payments/callbacks/${method}`
}

/**
 * Public: an operator has no credential of ours. A notification only
 * prompts a status check with the operator; its claim is never
 * trusted. Bodies over 64 KiB are refused (413), and a method with a
 * sender allow-list refuses other addresses (403 `callback_rejected`).
 * @summary An operator's payment notification
 */
export const receivePaymentCallback = async (method: PaymentMethod,
    receivePaymentCallbackBody?: ReceivePaymentCallbackBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getReceivePaymentCallbackUrl(method),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(receivePaymentCallbackBody)
  }
);}





export const getReceivePaymentCallbackMutationKey = () => ['receivePaymentCallback'] as const;

export const getReceivePaymentCallbackMutationOptions = <TError = ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receivePaymentCallback>>, TError,ReceivePaymentCallbackMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receivePaymentCallback>>, TError,ReceivePaymentCallbackMutationVariables, TContext> => {

const mutationKey = getReceivePaymentCallbackMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receivePaymentCallback>>, ReceivePaymentCallbackMutationVariables> = (props) => {
          const {method,data} = props ?? {};

          return  receivePaymentCallback(method,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReceivePaymentCallbackMutationResult = NonNullable<Awaited<ReturnType<typeof receivePaymentCallback>>>
    export type ReceivePaymentCallbackMutationBody = ReceivePaymentCallbackBody | undefined
    export type ReceivePaymentCallbackMutationError = ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type ReceivePaymentCallbackMutationVariables = {method: PaymentMethod;data?: ReceivePaymentCallbackBody}

    /**
 * @summary An operator's payment notification
 */
export const useReceivePaymentCallback = <TError = ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receivePaymentCallback>>, TError,ReceivePaymentCallbackMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof receivePaymentCallback>>,
        TError,
        ReceivePaymentCallbackMutationVariables,
        TContext
      > => {
      return useMutation(getReceivePaymentCallbackMutationOptions(options), queryClient);
    }
    export const getReceivePaymentCallbackPutUrl = (method: PaymentMethod,) => {




  return `/payments/callbacks/${method}`
}

/**
 * MTN MoMo notifies with PUT; otherwise the same as POST.
 * @summary An operator's payment notification, sent with PUT
 */
export const receivePaymentCallbackPut = async (method: PaymentMethod,
    receivePaymentCallbackPutBody?: ReceivePaymentCallbackPutBody, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<void>(getReceivePaymentCallbackPutUrl(method),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(receivePaymentCallbackPutBody)
  }
);}





export const getReceivePaymentCallbackPutMutationKey = () => ['receivePaymentCallbackPut'] as const;

export const getReceivePaymentCallbackPutMutationOptions = <TError = ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receivePaymentCallbackPut>>, TError,ReceivePaymentCallbackPutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receivePaymentCallbackPut>>, TError,ReceivePaymentCallbackPutMutationVariables, TContext> => {

const mutationKey = getReceivePaymentCallbackPutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receivePaymentCallbackPut>>, ReceivePaymentCallbackPutMutationVariables> = (props) => {
          const {method,data} = props ?? {};

          return  receivePaymentCallbackPut(method,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReceivePaymentCallbackPutMutationResult = NonNullable<Awaited<ReturnType<typeof receivePaymentCallbackPut>>>
    export type ReceivePaymentCallbackPutMutationBody = ReceivePaymentCallbackPutBody | undefined
    export type ReceivePaymentCallbackPutMutationError = ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type ReceivePaymentCallbackPutMutationVariables = {method: PaymentMethod;data?: ReceivePaymentCallbackPutBody}

    /**
 * @summary An operator's payment notification, sent with PUT
 */
export const useReceivePaymentCallbackPut = <TError = ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receivePaymentCallbackPut>>, TError,ReceivePaymentCallbackPutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof receivePaymentCallbackPut>>,
        TError,
        ReceivePaymentCallbackPutMutationVariables,
        TContext
      > => {
      return useMutation(getReceivePaymentCallbackPutMutationOptions(options), queryClient);
    }
    export const getAdminListPaymentsUrl = (params?: AdminListPaymentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/payments?${stringifiedParams}` : `/admin/payments`
}

/**
 * @summary Payments, newest first
 */
export const adminListPayments = async (params?: AdminListPaymentsParams, options?: Parameters<typeof apiFetch>[1]): Promise<AdminPaymentPage> => {

  return apiFetch<AdminPaymentPage>(getAdminListPaymentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListPaymentsInfiniteQueryKey = (params?: AdminListPaymentsParams,) => {
    return [
    'infinite', `/admin/payments`, ...(params ? [params] : [])
    ] as const;
    }

export const getAdminListPaymentsQueryKey = (params?: AdminListPaymentsParams,) => {
    return [
    `/admin/payments`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminListPaymentsInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof adminListPayments>>, AdminListPaymentsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListPaymentsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData, QueryKey, AdminListPaymentsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListPaymentsInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListPayments>>, QueryKey, AdminListPaymentsParams['cursor']> = ({ signal, pageParam }) => adminListPayments({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData, QueryKey, AdminListPaymentsParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListPaymentsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof adminListPayments>>>
export type AdminListPaymentsInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListPaymentsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListPayments>>, AdminListPaymentsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListPaymentsParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData, QueryKey, AdminListPaymentsParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPayments>>,
          TError,
          Awaited<ReturnType<typeof adminListPayments>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPaymentsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListPayments>>, AdminListPaymentsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData, QueryKey, AdminListPaymentsParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPayments>>,
          TError,
          Awaited<ReturnType<typeof adminListPayments>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPaymentsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListPayments>>, AdminListPaymentsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData, QueryKey, AdminListPaymentsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Payments, newest first
 */

export function useAdminListPaymentsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListPayments>>, AdminListPaymentsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData, QueryKey, AdminListPaymentsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListPaymentsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListPaymentsQueryOptions = <TData = Awaited<ReturnType<typeof adminListPayments>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListPaymentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListPaymentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListPayments>>> = ({ signal }) => adminListPayments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListPaymentsQueryResult = NonNullable<Awaited<ReturnType<typeof adminListPayments>>>
export type AdminListPaymentsQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListPayments<TData = Awaited<ReturnType<typeof adminListPayments>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListPaymentsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPayments>>,
          TError,
          Awaited<ReturnType<typeof adminListPayments>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPayments<TData = Awaited<ReturnType<typeof adminListPayments>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPayments>>,
          TError,
          Awaited<ReturnType<typeof adminListPayments>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPayments<TData = Awaited<ReturnType<typeof adminListPayments>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Payments, newest first
 */

export function useAdminListPayments<TData = Awaited<ReturnType<typeof adminListPayments>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPayments>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListPaymentsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListPaymentsToReviewUrl = (params?: AdminListPaymentsToReviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/payments/review?${stringifiedParams}` : `/admin/payments/review`
}

/**
 * @summary The payments waiting for a person
 */
export const adminListPaymentsToReview = async (params?: AdminListPaymentsToReviewParams, options?: Parameters<typeof apiFetch>[1]): Promise<AdminPaymentPage> => {

  return apiFetch<AdminPaymentPage>(getAdminListPaymentsToReviewUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListPaymentsToReviewInfiniteQueryKey = (params?: AdminListPaymentsToReviewParams,) => {
    return [
    'infinite', `/admin/payments/review`, ...(params ? [params] : [])
    ] as const;
    }

export const getAdminListPaymentsToReviewQueryKey = (params?: AdminListPaymentsToReviewParams,) => {
    return [
    `/admin/payments/review`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminListPaymentsToReviewInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof adminListPaymentsToReview>>, AdminListPaymentsToReviewParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListPaymentsToReviewParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData, QueryKey, AdminListPaymentsToReviewParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListPaymentsToReviewInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListPaymentsToReview>>, QueryKey, AdminListPaymentsToReviewParams['cursor']> = ({ signal, pageParam }) => adminListPaymentsToReview({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData, QueryKey, AdminListPaymentsToReviewParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListPaymentsToReviewInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof adminListPaymentsToReview>>>
export type AdminListPaymentsToReviewInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListPaymentsToReviewInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListPaymentsToReview>>, AdminListPaymentsToReviewParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListPaymentsToReviewParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData, QueryKey, AdminListPaymentsToReviewParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPaymentsToReview>>,
          TError,
          Awaited<ReturnType<typeof adminListPaymentsToReview>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPaymentsToReviewInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListPaymentsToReview>>, AdminListPaymentsToReviewParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsToReviewParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData, QueryKey, AdminListPaymentsToReviewParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPaymentsToReview>>,
          TError,
          Awaited<ReturnType<typeof adminListPaymentsToReview>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPaymentsToReviewInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListPaymentsToReview>>, AdminListPaymentsToReviewParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsToReviewParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData, QueryKey, AdminListPaymentsToReviewParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The payments waiting for a person
 */

export function useAdminListPaymentsToReviewInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListPaymentsToReview>>, AdminListPaymentsToReviewParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsToReviewParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData, QueryKey, AdminListPaymentsToReviewParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListPaymentsToReviewInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListPaymentsToReviewQueryOptions = <TData = Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListPaymentsToReviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListPaymentsToReviewQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListPaymentsToReview>>> = ({ signal }) => adminListPaymentsToReview(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListPaymentsToReviewQueryResult = NonNullable<Awaited<ReturnType<typeof adminListPaymentsToReview>>>
export type AdminListPaymentsToReviewQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListPaymentsToReview<TData = Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListPaymentsToReviewParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPaymentsToReview>>,
          TError,
          Awaited<ReturnType<typeof adminListPaymentsToReview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPaymentsToReview<TData = Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsToReviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListPaymentsToReview>>,
          TError,
          Awaited<ReturnType<typeof adminListPaymentsToReview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListPaymentsToReview<TData = Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsToReviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The payments waiting for a person
 */

export function useAdminListPaymentsToReview<TData = Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListPaymentsToReviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListPaymentsToReview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListPaymentsToReviewQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminGetPaymentUrl = (id: string,) => {




  return `/admin/payments/${id}`
}

/**
 * @summary One payment with its operator exchanges and journal
 */
export const adminGetPayment = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<AdminPaymentDetail> => {

  return apiFetch<AdminPaymentDetail>(getAdminGetPaymentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminGetPaymentQueryKey = (id: string,) => {
    return [
    `/admin/payments/${id}`
    ] as const;
    }


export const getAdminGetPaymentQueryOptions = <TData = Awaited<ReturnType<typeof adminGetPayment>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetPayment>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminGetPaymentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminGetPayment>>> = ({ signal }) => adminGetPayment(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminGetPayment>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminGetPaymentQueryResult = NonNullable<Awaited<ReturnType<typeof adminGetPayment>>>
export type AdminGetPaymentQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse


export function useAdminGetPayment<TData = Awaited<ReturnType<typeof adminGetPayment>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetPayment>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetPayment>>,
          TError,
          Awaited<ReturnType<typeof adminGetPayment>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetPayment<TData = Awaited<ReturnType<typeof adminGetPayment>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetPayment>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetPayment>>,
          TError,
          Awaited<ReturnType<typeof adminGetPayment>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetPayment<TData = Awaited<ReturnType<typeof adminGetPayment>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetPayment>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One payment with its operator exchanges and journal
 */

export function useAdminGetPayment<TData = Awaited<ReturnType<typeof adminGetPayment>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetPayment>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminGetPaymentQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminReconcilePaymentUrl = (id: string,) => {




  return `/admin/payments/${id}/reconcile`
}

/**
 * @summary Ask the operator for a payment's status now
 */
export const adminReconcilePayment = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<AdminPayment> => {

  return apiFetch<AdminPayment>(getAdminReconcilePaymentUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAdminReconcilePaymentMutationKey = () => ['adminReconcilePayment'] as const;

export const getAdminReconcilePaymentMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminReconcilePayment>>, TError,AdminReconcilePaymentMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminReconcilePayment>>, TError,AdminReconcilePaymentMutationVariables, TContext> => {

const mutationKey = getAdminReconcilePaymentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminReconcilePayment>>, AdminReconcilePaymentMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  adminReconcilePayment(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminReconcilePaymentMutationResult = NonNullable<Awaited<ReturnType<typeof adminReconcilePayment>>>

    export type AdminReconcilePaymentMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse
    export type AdminReconcilePaymentMutationVariables = {id: string}

    /**
 * @summary Ask the operator for a payment's status now
 */
export const useAdminReconcilePayment = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminReconcilePayment>>, TError,AdminReconcilePaymentMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminReconcilePayment>>,
        TError,
        AdminReconcilePaymentMutationVariables,
        TContext
      > => {
      return useMutation(getAdminReconcilePaymentMutationOptions(options), queryClient);
    }
    export const getAdminResolvePaymentUrl = (id: string,) => {




  return `/admin/payments/${id}/resolve`
}

/**
 * `credit` settles the payment's own top-up (it needs the operator
 * reference attested unless the operator confirmed the money); `close`
 * closes it unpaid. `credit_as_topup` credits money that arrived after
 * the payment and its top-up closed (a failed payment then paid, or 409
 * `topup_closed` on `credit`) as a top-up of its own: a paid, refundable
 * lot, a numbered invoice, VAT and the operator's clearing leg. It needs
 * the attested reference, is refused while the payment can still settle
 * its own top-up (409 `payment_creditable`), and is allowed once per
 * payment (409 `payment_already_credited`). Staff never credit their own
 * account, nor one of a user they could not manage (403
 * `privilege_escalation`).
 * @summary Settle a payment held for review by hand
 */
export const adminResolvePayment = async (id: string,
    adminResolvePaymentBody: AdminResolvePaymentBody, options?: Parameters<typeof apiFetch>[1]): Promise<AdminPayment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<AdminPayment>(getAdminResolvePaymentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminResolvePaymentBody)
  }
);}





export const getAdminResolvePaymentMutationKey = () => ['adminResolvePayment'] as const;

export const getAdminResolvePaymentMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminResolvePayment>>, TError,AdminResolvePaymentMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminResolvePayment>>, TError,AdminResolvePaymentMutationVariables, TContext> => {

const mutationKey = getAdminResolvePaymentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminResolvePayment>>, AdminResolvePaymentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  adminResolvePayment(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminResolvePaymentMutationResult = NonNullable<Awaited<ReturnType<typeof adminResolvePayment>>>
    export type AdminResolvePaymentMutationBody = AdminResolvePaymentBody
    export type AdminResolvePaymentMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminResolvePaymentMutationVariables = {id: string;data: AdminResolvePaymentBody}

    /**
 * @summary Settle a payment held for review by hand
 */
export const useAdminResolvePayment = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminResolvePayment>>, TError,AdminResolvePaymentMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminResolvePayment>>,
        TError,
        AdminResolvePaymentMutationVariables,
        TContext
      > => {
      return useMutation(getAdminResolvePaymentMutationOptions(options), queryClient);
    }
